/**
 * PR13-C (Visual Feedback V2): fixes the known latent gate-glow lifecycle
 * bug - the gate's open-glow tween was previously created with a
 * `!this.gateWasOpen` guard but never explicitly killed, so a
 * close/reopen cycle (or a state change while already open) could stack
 * a second infinite-repeat tween on top of the first. This manager owns
 * exactly one active tween at a time: every `setState()` call kills
 * whatever tween it currently holds before optionally starting a new one,
 * so "kill and release old gate tweens before creating a replacement" is
 * true by construction, not by convention.
 *
 * Deliberately generic over a minimal tween-host shape (not a real
 * `Phaser.Scene`) so this is unit-testable with a plain fake - see
 * `__tests__/gateGlowTween.test.ts`.
 */

export interface TweenHandle {
  stop(): void;
  /** "Tactile Impact Polish": optional so a test fake can omit them - a real Phaser Tween always has both. Used by `setPaused()` so the gate's repeat:-1 glow genuinely freezes on Pause instead of continuing to animate on Phaser's own clock underneath a frozen game. */
  pause?(): void;
  resume?(): void;
}

export interface TweenHost<TConfig> {
  add(config: TConfig): TweenHandle;
}

export type GateGlowState = "locked" | "open-permanent" | "open-temporary" | "warning";

export class GateGlowTweenManager<TConfig> {
  private current: TweenHandle | null = null;
  private currentState: GateGlowState = "locked";

  constructor(
    private readonly host: TweenHost<TConfig>,
    private readonly buildConfig: (state: Exclude<GateGlowState, "locked">) => TConfig,
  ) {}

  get state(): GateGlowState {
    return this.currentState;
  }

  /** True only while this manager actually holds a live tween handle - never true for `"locked"`, and never true twice in a row for the same unchanged state (a no-op `setState` call with the same state doesn't recreate the tween). */
  get hasActiveTween(): boolean {
    return this.current !== null;
  }

  setState(state: GateGlowState): void {
    if (state === this.currentState) return; // no-op: never recreate an already-correct tween.
    this.kill();
    this.currentState = state;
    if (state === "locked") return;
    this.current = this.host.add(this.buildConfig(state));
  }

  /** Restart/death/scene shutdown, or gate entry - releases the tween outright and resets to "locked" (drawing the locked-gate look, if any, is the caller's own responsibility). */
  kill(): void {
    if (this.current) {
      this.current.stop();
      this.current = null;
    }
  }

  reset(): void {
    this.kill();
    this.currentState = "locked";
  }

  /** Pauses/resumes the currently-held tween in place (never kills/recreates it) - so the gate's own alpha pulse genuinely freezes on Pause, exactly where it was, rather than continuing to animate on Phaser's own clock underneath a frozen game. A no-op when nothing is currently active. */
  setPaused(paused: boolean): void {
    if (!this.current) return;
    if (paused) this.current.pause?.();
    else this.current.resume?.();
  }
}
